import { useMutation, useQuery } from "@tanstack/react-query";
import {
  ADMIN_SUPPORT_MODULE_ID,
  ADMIN_SUPPORT_SOURCE_ID,
  LEARN_VERTICAL_ID,
  SCA_ONE_APP_MIGRATED,
  SCA_VERTICAL_ID,
} from "../constants/constants";
import { getAuthToken } from "./useAuth";

export const fetchUserOrders = async (uamId) => {
  const response = await fetch(
    `${process.env.REACT_APP_ONE_OMS_URL}/fetch-customer-orders-details`,
    {
      method: "POST",
      headers: {
        accept: "application/json",
        "secret-key": process.env.REACT_APP_ONE_OMS_SECRET_KEY,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ uam_id: uamId }),
    }
  );

  if (!response.ok) {
    throw new Error(
      `Failed to fetch data: ${response.status} ${response.statusText}`
    );
  }

  const result = await response.json();
  return result;
};

export const updateOrderStatus = async ({ orderId, action }) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ONE_OMS_URL}/update-order-cancellation-request/${orderId}/${action}`,
      {
        method: "POST",
        headers: {
          accept: "application/json",
          "secret-key": process.env.REACT_APP_ONE_OMS_SECRET_KEY,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Error: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    throw error;
  }
};

const uploadCsv = async ({ file, selectedScriptId, uploaderUserId }) => {
  const url = new URL(
    `${process.env.REACT_APP_UPLOAD_SERVICE_URL}/upload-file?uploaded_by=${uploaderUserId}&selected_script_id=${selectedScriptId}`
  );

  const headers = {
    accept: "application/json",
    "secret-key": `${process.env.REACT_APP_UPLOAD_SERVICE_SECRET_KEY}`,
  };

  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch(url, {
    method: "POST",
    headers,
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.message || `Failed to upload file. Status: ${response.status}`
    );
  }

  const data = await response.json();

  if (data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data.message || "An error occurred while discarding the order."
    );
  }
};

export const useUploadCsv = () => {
  return useMutation({ mutationFn: uploadCsv });
};

const fetchHistory = async (user_id) => {
  const url = new URL(
    `${process.env.REACT_APP_UPLOAD_SERVICE_URL}/get-all-files?uploader_user_id=${user_id}&module_id=${ADMIN_SUPPORT_MODULE_ID}`
  );

  const headers = {
    accept: "application/json",
    "secret-key": process.env.REACT_APP_UPLOAD_SERVICE_SECRET_KEY,
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "GET",
    headers: headers,
    body: null,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.message || `Failed to fetch history. Status: ${response.status}`
    );
  }

  const data = await response.json();

  if (data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data.message || "An error occurred while fetching pending orders."
    );
  }
};

export const useFetchHistory = (user_id) => {
  return useQuery({
    queryKey: ["history", user_id],
    queryFn: () => fetchHistory(user_id),
  });
};

const fetchProcessedFile = async (fileId) => {
  const url = new URL(
    `${process.env.REACT_APP_UPLOAD_SERVICE_URL}/download-processed-file/${fileId}`
  );

  const headers = {
    accept: "application/json",
    "secret-key": process.env.REACT_APP_UPLOAD_SERVICE_SECRET_KEY,
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "GET",
    headers: headers,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.message ||
        `Failed to fetch processed file. Status: ${response.status}`
    );
  }

  const data = await response.json();

  if (data.status === "success") {
    return { signedUrl: data.data.signed_url };
  } else {
    throw new Error(
      data.message || "An error occurred while fetching the processed file."
    );
  }
};

export const useFetchProcessedFile = (fileId) => {
  const query = useQuery({
    queryKey: ["processedFile", fileId],
    queryFn: () => fetchProcessedFile(fileId),
    enabled: !!fileId,
  });
  return {
    ...query,
    fetchProcessedFile,
  };
};

const unblockPhoneNumber = async (phoneNumber) => {
  const url = new URL(`${process.env.REACT_APP_RESET_OTP_URL}`);

  const payload = { isd_code: "+91", phone: phoneNumber };

  const headers = {
    accept: "application/json",
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });

  const data = await response.json();

  if (
    !data.success ||
    data.success.toLowerCase() !== "unblocked user successfully"
  ) {
    throw new Error(data.message || `Error: ${response.statusText}`);
  }

  return data.detail || data.success;
};

export const useUnblockPhoneNumber = () => {
  return useMutation({ mutationFn: unblockPhoneNumber });
};

const generateOtp = async ({
  phoneNumber,
  admissionNumber,
  productId,
  tenantId,
}) => {
  const url = new URL(`${process.env.REACT_APP_GET_OTP_URL}`);

  const payload = {
    isd_code: process.env.REACT_APP_ISD_CODE,
    phone: phoneNumber,
    admission_number: admissionNumber,
    tenant_id: tenantId,
    product_id: productId,
    mode: "SMS",
  };

  const headers = {
    accept: "application/json",
    "Content-Type": "application/json",
  };

  const response = await fetch(url, {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json();

    if (errorData.detail && Array.isArray(errorData.detail)) {
      const firstError = errorData.detail[0];
      const errorMessage =
        firstError.msg || "Validation error while generating OTP";
      throw new Error(errorMessage);
    }

    throw new Error(
      errorData.message || `Failed to generate OTP. Status: ${response.status}`
    );
  }
  const data = await response.json();
  if (data) {
    return data;
  } else {
    throw new Error(data.message || "An error occurred while generating OTP.");
  }
};
export const useGenerateOtp = () => {
  return useMutation({ mutationFn: generateOtp });
};

export const useCustomerInfoQuery = (payload) => {
  return useQuery({
    queryKey: ["customerInfo", payload],
    queryFn: () => fetchCustomerInfo(payload),
    enabled: Boolean(payload?.uam_id),
  });
};

const fetchCustomerOrderDetails = async (uamId) => {
  const url = new URL(
    `${process.env.REACT_APP_ONE_OMS_URL}/fetch-customer-orders-details`
  );

  const headers = {
    accept: "application/json",
    "secret-key": `${process.env.REACT_APP_ONE_OMS_SECRET_KEY}`,
    "Content-Type": "application/json",
  };

  const payload = { uam_id: uamId };

  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });

  const data = await response.json();

  if (data.status && data.status === "success") {
    return data.data.order_details;
  } else {
    throw new Error(
      data.message || "An error occurred while processing the request."
    );
  }
};

export const useFetchCustomerOrderDetails = (uamId) => {
  return useQuery({
    queryKey: ["customer_order_details", uamId],
    queryFn: () => fetchCustomerOrderDetails(uamId),
    enabled: !!uamId,
  });
};

const orderModificationSummary = async (orderId) => {
  const url = new URL(`${process.env.REACT_APP_ONE_OMS_URL}/get-order-modification-summary/${orderId}`);
  const headers = {
    accept: "application/json",
    "Content-Type": "application/json",
    "auth-token": getAuthToken(),
    "x-platform": "web",
  };
  const response = await fetch(url, {
    method: "GET",
    headers: headers,
  });
  const data = await response.json();
  if (data.status && data.status === "success") {
    return data.data;

  } else {
    const error = new Error(
      data.message || "An error occurred while processing the request."
    );
    error.statusCode = response.status;
    throw error;
  }
};
export const useOrderModificationSummary = (orderId) => {
  return useQuery({
    queryKey: ["orderModificationSummary", orderId],
    queryFn: () => orderModificationSummary(orderId),
    enabled: !!orderId
  });
};


const orderModificationDecision =async (payload)=>{
  const url = new URL(`${process.env.REACT_APP_ONE_OMS_URL}/update-order-modification-request`);
  const headers = {
    "Content-Type": "application/json",
    "auth-token": getAuthToken(),
    "x-source-id": ADMIN_SUPPORT_SOURCE_ID,
  };

  const response = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (data.status && data.status === "success") {
    return data.data;
  } else {
    throw new Error(
      data.message || "An error occurred while processing the request."
    );
  }
};
export const useOrderModificationDecision = () => {
return useMutation({
  mutationFn: (payload) => orderModificationDecision(payload),
});
}


const extractUserDetails = (globalUsers, selectedBusinessVerticalId) => {
  if (!globalUsers || !Array.isArray(globalUsers.accounts)) return [];

  if (!localStorage.getItem("phoneNumber")) {
    throw new Error(
      "Oops! Something doesn't look right. Please log out and log in again to continue.",
    );
  }

  const isSameUser = globalUsers.accounts.some(
    (account) => account.phone === localStorage.getItem("phoneNumber"),
  );

  if (isSameUser) {
    throw new Error(
      "Seems like you are creating an order for yourself. Please select a different user to create an order.",
    );
  }
  const filteredUsersByVertical = globalUsers.accounts.flatMap((account) =>
    account.profiles
      .filter(
        (profile) =>
          Number(profile.vertical_id) === Number(selectedBusinessVerticalId),
      )
      .map((profile) => ({
        firstName: account.first_name,
        lastName: account.last_name,
        phone: account.phone,
        verticalId: profile.vertical_id,
        uamId: profile.uam_id,
      })),
  );

  if (filteredUsersByVertical.length === 0) {
    throw new Error("No user found for the selected business vertical.");
  }

  return filteredUsersByVertical;
};

const fetchCustomerInfoFromOneApp = async (payload) => {
  const { phone_number, uam_id, selectedBusinessVerticalId } = payload;

  const url = process.env.REACT_APP_LEARN_USER_SEARCH_URL;

  const requestPayload = phone_number
    ? { isd_code: "+91", phone: phone_number }
    : { uam_id };

  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
    "product-id": "100",
    "tenant-id": "99999",
    platform: "web",
    Authorization: `Bearer ${getAuthToken()}`,
  };

  const response = await fetch(url, {
    method: "POST",
    headers,
    body: JSON.stringify(requestPayload),
  });

  if (response.status === 401) {
    throw new Error("Unauthorized: Please login again.");
  }

  if (response.status === 403) {
    throw new Error("Forbidden: You don't have access to this resource.");
  }
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to fetch customer info: ${response.status} ${errorText}`,
    );
  }

  const responseData = await response.json();

  return extractUserDetails(responseData, selectedBusinessVerticalId);
};

const formatKHUserToUserFormat = (users) => {
  const formattedUsers = users.map((account) => {
    return {
      firstName: account.first_name,
      lastName: account.last_name,
      phone: account.phone,
      verticalId: SCA_VERTICAL_ID,
      uamId: account.uam_id,
    };
  });

  return formattedUsers;
};
const fetchKHCustomerInfo = async (payload) => {
  const url = new URL(process.env.REACT_APP_SCA_USER_SEARCH_URL);

  const headers = {
    accept: "application/json",
    "product-id": "900",
    platform: "web",
    "tenant-id": "7",
    Authorization: `Bearer ${getAuthToken()}`,
    "Content-Type": "application/json",
  };

  const requestPayload = payload.phone_number
    ? { isd_code: "+91", phone: payload.phone_number }
    : { uam_id: payload.uam_id };

  const response = await fetch(url, {
    method: "POST",
    headers,
    body: JSON.stringify(requestPayload),
  });

  if (response.status === 401) {
    throw new Error("Unauthorized: Please login again.");
  }

  if (response.status === 403) {
    throw new Error("Forbidden: You don't have access to this resource.");
  }

  if (!response.ok) {
    throw new Error(
      "Failed to fetch KH customer info, please try again later.",
    );
  }

  const data = await response.json();

  return formatKHUserToUserFormat(data.users);
};

const fetchCustomerInfoSeparately = async (payload) => {
  if (Number(payload.selectedBusinessVerticalId) === Number(LEARN_VERTICAL_ID)) {
    return fetchCustomerInfoFromOneApp(payload);
  }

  return fetchKHCustomerInfo(payload);
};

const fetchCustomerInfo = async (payload) => {
  return SCA_ONE_APP_MIGRATED === true
    ? fetchCustomerInfoFromOneApp(payload)
    : fetchCustomerInfoSeparately(payload);
};

export const useFetchCustomerInfo = () => {
  return useMutation({ mutationFn: fetchCustomerInfo });
};
